import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import './TopBarDropdown.css';

type SubLink = {
  label: string;
  to: string;
  isActive?: (pathname: string) => boolean;
};

type TopBarDropdownProps = {
  icon: React.ReactNode;
  title: string;
  subLinks: SubLink[];
  isProd: boolean;
  basePath: string;
  onLinkClick: () => void;
};

const TopBarDropdown = ({
  basePath,
  icon,
  isProd,
  onLinkClick,
  subLinks,
  title,
}: TopBarDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isParentActive = pathname.startsWith(basePath);
  const activeClassName = isProd ? 'prod-active' : 'sandbox-active';

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      className="topbar-dropdown-container"
      ref={dropdownRef}
    >
      <button
        className={`menu-link dropdown-toggle ${isParentActive ? activeClassName : ''}`}
        onClick={handleToggle}
        type="button"
      >
        <div className="dropdown-toggle-left">
          {icon}
          <span>{title}</span>
        </div>
        <div className="dropdown-toggle-right">
          <svg
            className="chevron-icon"
            fill="none"
            height="16"
            viewBox="0 0 24 24"
            width="16"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
            />
          </svg>
        </div>
      </button>
      <div className={`dropdown-menu ${isOpen ? 'show' : ''}`}>
        <div className="dropdown-menu-inner">
          {subLinks.map((link) => (
            <NavLink
              activeClassName="active"
              className="dropdown-item"
              exact={!link.isActive}
              isActive={() => (link.isActive ? link.isActive(pathname) : false)}
              key={link.to}
              onClick={() => {
                onLinkClick();
                handleClose();
              }}
              to={link.to}
            >
              {link.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopBarDropdown;